import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const TransportationCost = () => {
  // State variables for Transportation Cost calculation
  const [distance, setDistance] = useState(0); // Distance to be traveled
  const [ratePerMile, setRatePerMile] = useState(0); // Rate per mile or kilometer
  const [transportationCost, setTransportationCost] = useState(0); // Calculated transportation cost

  // Event handlers to update state values
  const handleDistanceChange = (e) => setDistance(parseFloat(e.target.value) || 0);
  const handleRatePerMileChange = (e) => setRatePerMile(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate transportation cost when any input changes
  useEffect(() => {

    const cost = distance * ratePerMile;
    setTransportationCost(cost);

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [distance, ratePerMile]);

  return (
    <Layout title="Transportation Cost Calculator" path="transportation-cost">
      <section className="section-padding" id="transportation-cost-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Transportation Cost Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Transportation Cost Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Distance Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="distance" className="form-label">
                        Distance (Miles/Km)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Distance:</strong> The distance refers to the total length of the route to be traveled, either in miles or kilometers."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="distance"
                        placeholder="Enter distance to be traveled"
                        value={distance || ""}
                        onChange={handleDistanceChange}
                      />
                    </div>

                    {/* Rate per Mile or Kilometer Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="ratePerMile" className="form-label">
                        Rate per Mile/Km
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Rate per Mile/Km:</strong> The rate refers to the cost charged for traveling one mile or kilometer."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="ratePerMile"
                        placeholder="Enter rate per mile/kilometer"
                        value={ratePerMile || ""}
                        onChange={handleRatePerMileChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Total Transportation Cost:{" "}
                        <span className="fw-bold ms-3">
                          {transportationCost === 0 ? "0.00" : transportationCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Transportation Cost Calculator
                </h3>

                {/* What is a Transportation Cost Calculator? */}
                <h4 className="mt-3 mb-3">What is a Transportation Cost Calculator?</h4>
                <p>
                  A **Transportation Cost Calculator** helps businesses determine the cost of transporting goods over a specified distance. It is calculated by multiplying the distance to be traveled by the rate per mile or kilometer.
                </p>

                {/* Why Use a Transportation Cost Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Transportation Cost Calculator?</h4>
                <p>
                  This calculator helps businesses in budgeting logistics expenses and estimating freight and delivery costs. It provides clarity on the costs involved in the transportation of goods, which is essential for maintaining profitable logistics operations.
                </p>

                {/* Benefits of Using a Transportation Cost Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Transportation Cost Calculator</h4>
                <ul>
                  <li><strong>Helps Budget Logistics Expenses:</strong> Allows businesses to accurately estimate and manage their transportation costs.</li>
                  <li><strong>Useful for Freight and Delivery Cost Estimates:</strong> Helps calculate transportation expenses based on distance and rate, assisting in pricing decisions.</li>
                  <li><strong>Supports Supply Chain Management:</strong> Provides insight into the cost of delivering goods, supporting cost optimization in supply chains.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Transportation Cost Calculator?</h4>
                <p>
                  This calculator is suitable for:
                </p>
                <ul>
                  <li><strong>Logistics Companies:</strong> Helps calculate and manage transportation expenses for deliveries.</li>
                  <li><strong>Retail Businesses:</strong> Assists retailers in estimating shipping costs for online orders.</li>
                  <li><strong>Shipping and Freight Companies:</strong> Useful for freight companies to estimate and calculate transport costs.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">How can I reduce transportation costs?</h4>
                <p>
                  You can reduce transportation costs by optimizing delivery routes, negotiating better rates with carriers, or consolidating shipments to reduce the number of trips needed.
                </p>

                <h4 className="mt-4 mb-3">What factors affect transportation costs?</h4>
                <p>
                  Several factors affect transportation costs, including distance, weight of goods, fuel prices, tolls, and the type of transportation used (e.g., truck, rail, air, or sea).
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TransportationCost;
